import { useRef } from "react"

import { HelpCenterContent, HelpCenterHeader, WikiSection } from "@/areas/help-center"
import Article from "@/shared/layouts/Article/Article"

function CbsNorthStarSetup() {
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <HelpCenterContent ref={contentRef}>
      <HelpCenterHeader
        title="CBS-NorthStar Setup"
        subtitle="What you need to configure inside CBS-NorthStar POS so it can send orders correctly."
        logo="/static/wiki/cbs-northstar-logo.png"
        backPath="/help-center/pos-configuration"
        backLabel="POS Configuration Guide"
        contentRef={contentRef}
      />

      <WikiSection id="overview" title="OVERVIEW">
        <Article>
          <p>This document provides a step-by-step process for:</p>
          <ul className="wiki-list">
            <li>Connecting CBS-NorthStar POS to the OCB App.</li>
            <li>Validating live order transmission between CBS-NorthStar and OCB App.</li>
            <li>Installing and configuring the client&apos;s CBS-NorthStar POS.</li>
          </ul>

          <h3>Requirements</h3>
          <ul className="wiki-list">
            <li><strong>CBS-NorthStar</strong> necessary credentials;</li>
          </ul>
        </Article>
      </WikiSection>

      <WikiSection id="open-ocb-app" title="A — OPEN THE OCB APP AND ACCESS THE CONFIGURATION FLOW">
        <Article>
          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Open the OCB Desktop App on the company&apos;s Windows machine.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>When the app launches, a Welcome screen is displayed.</p>
          </div>

          <ul className="wiki-list">
            <li>At this point, the app is not yet configured with any Point of Sale (POS).</li>
            <li>This screen only confirms that the app is installed and running correctly.</li>
          </ul>

          <div className="wiki-image-single">
            <img src="/static/wiki/cbs-northstar/cbs-01.png" alt="OCB Welcome screen" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click <strong>Ask</strong> to proceed. (You will see the Welcome preview screen, but configuration is still pending.)</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click <strong>Back to the main menu</strong> to begin the initial POS setup process.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/cbs-northstar/cbs-02.png" alt="Welcome preview and settings menu" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Log in to the OCB App:</p>
          </div>

          <ul className="wiki-list">
            <li>Enter the <strong>default</strong> password: <strong>1234</strong></li>
            <li>Click <strong>Login</strong> to continue.</li>
          </ul>

          <div className="wiki-image-single">
            <img src="/static/wiki/cbs-northstar/cbs-03.png" alt="OCB login screen" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>

      <WikiSection id="configure-ocb-app" title="B - CONFIGURE OCB APP FOR CBS-NORTHSTAR POS">
        <Article>
          <h3>Select Oracle Integration</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Choose the <strong>POS integration</strong> type:</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>From the list of available integrations, select <strong>CBS-NorthStar</strong> Integration.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click the <strong>Next</strong> button to proceed.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/cbs-northstar/cbs-04.png" alt="Choose Point of Sales screen" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/cbs-northstar/cbs-05.png" alt="CBS-NorthStar integration selected" className="wiki-screenshot" />
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/cbs-northstar/cbs-06.png" alt="Choose Point of Sales with Next button" className="wiki-screenshot" />
          </div>

          <h3>Define Integration Parameters</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Fill in the required Oracle fields:</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p><strong>POS ID</strong> → Secure API token provided by CBS-NorthStar POS, used to authenticate and authorize API requests.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p><strong>Token</strong> → Authentication token provided by CBS-NorthStar POS.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p><strong>Enable Soundhound</strong> → Enable SoundHound if you would like to use voice-related features.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click the <strong>Next</strong> button to continue, then <strong>Confirm</strong> to save the configuration.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/cbs-northstar/cbs-07.png" alt="Enter POS ID and Token" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/cbs-northstar/cbs-08.png" alt="Confirm your choices" className="wiki-screenshot" />
          </div>

          <h3>Validate OCB App Status</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Confirm successful setup:</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After confirmation, you will be redirected to the <strong>Welcome Screen</strong>.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>This indicates the <strong>OCB App</strong> is correctly configured and running.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/cbs-northstar/cbs-09.png" alt="Welcome screen after successful configuration" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>

      <WikiSection id="menu-app-setup" title="C - MENU APP ONLY: CBS-NORTHSTAR SETUP IN STREAM-EDITOR">
        <Article>
          <p>This section explains how to configure the CBS-NorthStar integration specifically for the <strong>Menu App</strong> within Stream-Editor. Follow the steps below only if you are setting up the Menu App.</p>

          <h3>Log In to Stream-Editor</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Open your browser, navigate to your <strong>Stream-Editor URL</strong>, and log in with your credentials.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click at <strong>OCB Settings</strong>.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Under <strong>Locations</strong>, choose a location or create a new one and click on the <strong>⚙️ gear icon</strong> next to the location to open the Integrations panel.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/cbs-northstar/cbs-10.png" alt="OCB Settings locations list" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/cbs-northstar/cbs-11.png" alt="Menu Settings with gear icon" className="wiki-screenshot" />
          </div>

          <h3>Connect Table Needs</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>From the the Integrations panel list, select <strong>CBS-NorthStar</strong> and click on the <strong>Next</strong> button.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/cbs-northstar/cbs-12.png" alt="Create Integration - Select CBS-NorthStar" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/cbs-northstar/cbs-13.png" alt="CBS-NorthStar selected with Next button" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Provide the required Oracle POS details in each field, based on <strong>your organization&apos;s CBS-NorthStar configuration</strong>.</p>
          </div>

          <ul className="wiki-list">
            <li><strong>POS Client ID:</strong> The Client ID provided by CBS-NorthStar POS. This unique identifier allows your application to authenticate and interact with the CBS-NorthStar POS API.</li>
            <li><strong>POS API URL:</strong> The URL endpoint of the CBS-NorthStar POS API. This base URL is necessary for making API requests to the POS system.</li>
            <li><strong>POS API Token:</strong> The API token provided by CBS-NorthStar POS. This token is used to authenticate and authorize API requests, ensuring secure interaction with the POS system.</li>
          </ul>

          <div className="wiki-image-single">
            <img src="/static/wiki/cbs-northstar/cbs-14.png" alt="CBS-NorthStar Integration form" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once all fields are filled in, click on the <strong>Create</strong> button to complete the integration.</p>
          </div>
        </Article>
      </WikiSection>
    </HelpCenterContent>
  )
}

export default CbsNorthStarSetup
